import { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Search, Plus, Trash2, Check, IndianRupee, Wallet, Smartphone, Landmark,
  Receipt, User, CreditCard, FileText, Printer, Download, PartyPopper, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import api, { fmtINR, fmtNumber, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import SuggestInput from "@/components/SuggestInput";
import { queueAdd, isOfflineError } from "@/lib/offlineQueue";

const PAYMENT_MODES = [
  { id: "petty", label: "Petty cash", icon: Wallet, tone: "amber" },
  { id: "online", label: "Online cash", icon: Smartphone, tone: "cyan" },
  { id: "bank", label: "Bank", icon: Landmark, tone: "cyan" },
  { id: "credit", label: "On Credit", icon: User, tone: "magenta" },
];

const ACCOUNT_MAP = {
  petty: "47fdb900-5c20-4f40-91d9-7bc5b84a09fa",
  online: "acc-cash",
  bank: "acc-hdfc",
  credit: "acc-credit",
};

/**
 * QuickBillModal — non-GST simple cash memo.
 * Single-step form for speed. Uses products from catalog OR free-text items.
 * Total = sum(qty × rate). No GST. No HSN.
 */
export default function QuickBillModal({ open, onClose, onCreated }) {
  const [company, setCompany] = useState(null);
  const [customer, setCustomer] = useState({ name: "Walk-in Customer", mobile: "" });
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [paymentMode, setPaymentMode] = useState("petty");
  const [paid, setPaid] = useState(0);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [billDate, setBillDate] = useState(new Date().toISOString().slice(0, 10));
  const [qrAccounts, setQrAccounts] = useState([]);
  const [receiveId, setReceiveId] = useState("company");
  const [qrImg, setQrImg] = useState(null);
  const [scan, setScan] = useState(null);
  const [uploadingScan, setUploadingScan] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const scanInputRef = useRef(null);
  const searchRef = useRef(null);
  const [custSuggest, setCustSuggest] = useState(null);
  const [custId, setCustId] = useState(null);
  const [custType, setCustType] = useState("");
  const [splitOn, setSplitOn] = useState(false);
  const [splitCash, setSplitCash] = useState("");
  const [splitOnl, setSplitOnl] = useState("");
  const [splitOnlMode, setSplitOnlMode] = useState("online");
  const [typePrices, setTypePrices] = useState({});
  const [lastRates, setLastRates] = useState({});
  const [camOpen, setCamOpen] = useState(false);
  useEffect(() => { (async () => { try { const { data } = await api.get("/customer-type-prices"); setTypePrices(data.map || {}); } catch {} })(); }, []);
  const rateFor = (p) => {
    const key = Object.keys(typePrices).find((k) => k.toLowerCase() === String(custType || "").toLowerCase());
    const v = key ? Number(p[typePrices[key]]) : 0;
    return v > 0 ? v : Number(p.selling_price || p.retail_price || 0);
  };
  const [discountVal, setDiscountVal] = useState("");
  const [showSqft, setShowSqft] = useState(false);
  const [sq, setSq] = useState({ name: "", l: "", b: "", rate: "" });

  // Customer auto-suggest (mobile se purana customer + due)
  useEffect(() => {
    const m = (customer.mobile || "").replace(/\D/g, "");
    if (m.length < 4) { setCustSuggest(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers", { params: { q: m, limit: 3 } });
        const hit = (data || []).find((c) => (c.mobile || "").replace(/\D/g, "").includes(m));
        setCustSuggest(hit && hit.id !== custId ? hit : null);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [customer.mobile, custId]);

  // #2 purane customer ke pichle rate
  useEffect(() => {
    if (!custId) { setLastRates({}); return; }
    (async () => {
      try { const { data } = await api.get(`/customers/${custId}/last-rates`); setLastRates(data.rates || {}); }
      catch { setLastRates({}); }
    })();
  }, [custId]);

  const applySuggest = () => {
    if (!custSuggest) return;
    setCustomer({ name: custSuggest.name, mobile: custSuggest.mobile || customer.mobile });
    setCustId(custSuggest.id);
    setCustType(custSuggest.type || "");
    setCustSuggest(null);
  };

  const applyDiscount = (v) => {
    setDiscountVal(v);
    const n = Number(v) || 0;
    setItems((arr) => {
      const rest = arr.filter((i) => i.product_id !== "discount-line");
      return n > 0 ? [...rest, { product_id: "discount-line", name: "Discount", sku: "", hsn: "", unit: "", qty: 1, rate: -n, gst: 0, discount: 0 }] : rest;
    });
  };

  const addSqftItem = () => {
    const L = Number(sq.l), B = Number(sq.b), R = Number(sq.rate);
    if (!sq.name.trim() || !(L > 0) || !(B > 0)) { toast.error("Fill in Name, Length, Width"); return; }
    const area = Math.round(L * B * 100) / 100;
    setItems((arr) => [...arr, { product_id: `manual-${Date.now()}`, name: `${sq.name.trim()} (${L}x${B} ft)`, sku: "", hsn: "", unit: "sqft", qty: area, rate: R || 0, gst: 0, discount: 0 }]);
    setShowSqft(false); setSq({ name: "", l: "", b: "", rate: "" });
    searchRef.current?.focus();
  };

  // 3-min inactivity auto-close (kiosk security)
  useEffect(() => {
    if (!open) return;
    let t = setTimeout(() => onClose?.(), 180000);
    const reset = () => { clearTimeout(t); t = setTimeout(() => onClose?.(), 180000); };
    window.addEventListener("pointerdown", reset);
    window.addEventListener("keydown", reset);
    return () => { clearTimeout(t); window.removeEventListener("pointerdown", reset); window.removeEventListener("keydown", reset); };
  }, [open, onClose]);
  const printRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setCustomer({ name: "Walk-in Customer", mobile: "" });
        setItems([]); setQ(""); setResults([]); setPaymentMode("cash"); setPaid(0); setCreated(null);
      if (null?.advance_used > 0) {
        toast.info(`Advance ${fmtINR(null.advance_used)} applied to this bill`, { description: "Previously deposited money" });
      } setSplitOn(false); setSplitCash(""); setSplitOnl("");
        setBillDate(new Date().toISOString().slice(0, 10)); setScan(null); setReceiveId("company"); setQrImg(null);
      }, 250);
      return;
    }
    (async () => { try { const { data } = await api.get("/company"); setCompany(data); } catch {} })();
    (async () => { try { const { data } = await api.get("/qr-accounts"); setQrAccounts(data); } catch {} })();
  }, [open]);

  useEffect(() => {
    const t = setTimeout(async () => {
      const query = q.trim();
      if (!query) { setResults([]); return; }
      try {
        const { data } = await api.get("/products", { params: { q: query, limit: 6 } });
        setResults(data);
        setShowDrop(true);
      } catch {}
    }, 160);
    return () => clearTimeout(t);
  }, [q]);

  const splitTotal = (Number(splitCash) || 0) + (Number(splitOnl) || 0);
  useEffect(() => { if (splitOn) setPaid(splitTotal); }, [splitOn, splitTotal]);

  const total = useMemo(() => items.reduce((s, it) => s + it.qty * it.rate, 0), [items]);

  useEffect(() => { if (splitOn) return; setPaid(paymentMode === "credit" ? 0 : Math.round(total)); }, [paymentMode, total, splitOn]);

  useEffect(() => {
    let revoke = null;
    (async () => {
      setQrImg(null);
      const acc = qrAccounts.find((a) => a.id === receiveId);
      if (!acc) return;
      if (acc.type === "company" && acc.qr_data) {
        setQrImg(`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(acc.qr_data)}&size=220x220&margin=8&bgcolor=ffffff&color=000000&qzone=1`);
      } else if (acc.type === "user") {
        try {
          const res = await api.get(`/users/${acc.id}/qr`, { responseType: "blob" });
          revoke = URL.createObjectURL(res.data);
          setQrImg(revoke);
        } catch {}
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [receiveId, qrAccounts]);

  const onScanPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploadingScan(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/scans", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScan({ id: data.scan_id, url: URL.createObjectURL(f), name: f.name });
      toast.success("Scan attached");
      setExtracting(true);
      try {
        const { data: ex } = await api.post(`/scans/${data.scan_id}/extract`);
        const fl = ex.fields || {};
        if (fl.customer_name || fl.customer_mobile) {
          setCustomer((c) => ({ ...c, name: fl.customer_name || c.name, mobile: fl.customer_mobile || c.mobile }));
        }
        if (fl.bill_date) setBillDate(fl.bill_date);
        if (Array.isArray(fl.items) && (fl.items || []).length) {
          setItems((fl.items || []).map((it, i) => ({ product_id: `manual-${Date.now()}-${i}`, name: String(it.name || "Item"), sku: "", hsn: "", unit: "piece", qty: Number(it.qty) || 1, rate: Number(it.rate) || 0, gst: 0 })));
        }
        if (fl.payment_mode) setPaymentMode(fl.payment_mode);
        if (Number(fl.paid) > 0) setTimeout(() => setPaid(Number(fl.paid)), 400);
        toast.success("Bill read automatically ✨", { description: "Har detail VERIFY karke hi save karna" });
      } catch (err2) {
        toast.info(formatApiErrorDetail(err2?.response?.data?.detail) || "Auto-read unavailable — photo dekh ke details bharo");
      } finally { setExtracting(false); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Scan upload failed");
    } finally { setUploadingScan(false); }
  };

  const addProduct = (p) => {
    const existing = items.findIndex((x) => x.product_id === p.id);
    if (existing >= 0) {
      const copy = [...items]; copy[existing].qty += 1; setItems(copy);
    } else {
      setItems([...items, { product_id: p.id, name: p.name, sku: p.sku || "", hsn: p.hsn || "", unit: p.unit || "piece", qty: 1, rate: rateFor(p), gst: 0 }]);
    }
    setQ(""); setResults([]); setShowDrop(false);
    searchRef.current?.focus();
  };

  const addManual = () => {
    if (!q.trim()) return;
    setItems([...items, { product_id: `manual-${Date.now()}`, name: q.trim(), sku: "", hsn: "", unit: "piece", qty: 1, rate: 0, gst: 0 }]);
    setQ(""); setResults([]); setShowDrop(false);
    searchRef.current?.focus();
  };

  const updateItem = (i, patch) => { const c = [...items]; c[i] = { ...c[i], ...patch }; setItems(c); };
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (items.length === 0) return;
    const _due = Math.max(0, Math.round(total) - (Number(paid) || 0));
    if (_due > 0 && !String(customer.mobile || "").replace(/\D/g, "")) {
      const go = window.confirm(`Is bill me \u20b9${_due.toLocaleString("en-IN")} udhaar hai par mobile number nahi hai.\n\nBina number ke na reminder bhej paoge, na ledger banega.\n\nPhir bhi save karein?`);
      if (!go) return;
    }
    setSaving(true);
    try {
      const body = {
        customer_name: customer.name.trim() || "Walk-in Customer",
        customer_mobile: customer.mobile,
        customer_type: custType || "retail",
        items: items.map((it) => ({
          product_id: String(it.product_id).startsWith("manual-") ? "manual" : it.product_id,
          name: it.name, sku: it.sku || "", hsn: it.hsn || "", unit: it.unit || "piece",
          qty: Number(it.qty), rate: Number(it.rate), gst: 0, discount: 0,
        })),
        bill_date: billDate,
        scan_id: scan?.id || null,
        source: scan ? "scan" : "app",
        ...(((paymentMode === "online" || paymentMode === "bank")) || (splitOn && (Number(splitOnl) || 0) > 0)
          ? (() => { const a = qrAccounts.find((x) => x.id === receiveId); return a ? { receive_user_id: a.type === "user" ? a.id : null, receive_label: a.label } : {}; })()
          : {}),
        payment_mode: splitOn ? (((Number(splitCash) || 0) > 0 && (Number(splitOnl) || 0) > 0) ? "split" : ((Number(splitCash) || 0) > 0 ? "cash" : splitOnlMode)) : paymentMode,
        payment_account_id: splitOn ? null : (ACCOUNT_MAP[paymentMode] || "acc-cash"),
        paid: Number(paid),
        customer_id: custId,
        payments: splitOn
          ? [{ mode: "cash", amount: Number(splitCash) || 0 }, { mode: splitOnlMode, amount: Number(splitOnl) || 0 }].filter((p) => p.amount > 0)
          : undefined,
        remarks: "Non-GST cash memo",
      };
      const { data } = await api.post("/invoices", body);
      setCreated(data);
      toast.success("Bill created", { description: `#${data.invoice_number}` });
      if (data.customer_auto_created) {
        toast.info("New customer saved 🗄", { description: `${data.customer_name} · a ledger will be created for them too` });
      }
      if (data.stock_warnings && (data.stock_warnings || []).length) {
        toast.warning("Check the stock record", { description: data.stock_warnings.join(" · "), duration: 8000 });
      }
      onCreated?.();
    } catch (e) {
      if (isOfflineError(e)) {
        const n = queueAdd(body);
        toast.success("🟧 No internet — bill saved safely", { description: `${n} bills waiting to sync. They'll be created automatically once you're online.` });
        setItems([]); setQ(""); setPaid(0); setCreated(null);
        onCreated?.();
        onClose?.();
        setSaving(false);
        return;
      }
      toast.error("Failed", { description: formatApiErrorDetail(e?.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const doPrint = () => {
    document.body.classList.add("print-memo");
    const done = () => { document.body.classList.remove("print-memo"); window.removeEventListener("afterprint", done); };
    window.addEventListener("afterprint", done);
    window.print();
    setTimeout(done, 3000);
  };
  const doThermal = () => {
    document.body.classList.add("print-thermal");
    const done = () => { document.body.classList.remove("print-thermal"); window.removeEventListener("afterprint", done); };
    window.addEventListener("afterprint", done);
    window.print();
    setTimeout(done, 3000);
  };
  const downloadPdf = async () => {
    const node = printRef.current;
    if (!node) return;
    const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff" });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const w = pdf.internal.pageSize.getWidth();
    const h = (canvas.height * w) / canvas.width;
    pdf.addImage(img, "PNG", 0, 0, w, h);
    pdf.save(`${created.invoice_number.replaceAll("/", "-")}.pdf`);
  };

  const sendWhatsApp = () => {
    if (!created) return;
    let mobile = (created.customer_mobile || "").replace(/\D/g, "");
    if (!mobile) {
      const entered = window.prompt("Customer WhatsApp number (10-digit or with country code):", "");
      if (!entered) return;
      mobile = entered.replace(/\D/g, "");
    }
    // Normalise to +91 if a 10-digit Indian number
    if (mobile.length === 10) mobile = "91" + mobile;
    if (mobile.length < 10) { toast.error("Enter a valid mobile number"); return; }

    const totalStr = `₹${Number(created.total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
    const paidStr = `₹${Number(created.paid || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
    const dueStr = Number(created.due || 0) > 0 ? `\nBalance due: ₹${Number(created.due).toLocaleString("en-IN")}` : "";
    const shopName = company?.name || "Interior Traders";
    const shopPhone = company?.phone || "";
    const lines = [
      `Hello ${created.customer_name || "Customer"},`,
      "",
      `Thank you for shopping at ${shopName}. Here is your cash memo:`,
      `Bill No: ${created.invoice_number}`,
      `Total: ${totalStr}`,
      `Paid: ${paidStr} (${(created.payment_mode || "").toUpperCase()})${dueStr}`,
      "",
      `Items:`,
      ...(created.items || []).slice(0, 8).map((it) => `• ${it.name} — ${it.qty} × ₹${Number(it.rate).toLocaleString("en-IN")}`),
      "",
      shopPhone ? `Reach us at ${shopPhone}.` : "",
      `Regards,\n${shopName}`,
    ].filter(Boolean).join("\n");

    const url = `https://wa.me/${mobile}?text=${encodeURIComponent(lines)}`;
    window.open(url, "_blank", "noopener");
    toast.success("WhatsApp opened", { description: `Chat pre-filled for +${mobile}` });
  };

  const newBill = () => {
    setCustomer({ name: "Walk-in Customer", mobile: "" });
    setItems([]); setQ(""); setPaymentMode("cash"); setPaid(0); setCreated(null); setSplitOn(false); setSplitCash(""); setSplitOnl("");
  };

  if (!open) return null;

  return (
    <>
    {camOpen && <BarcodeCam onClose={() => setCamOpen(false)} onCode={async (code) => {
      setCamOpen(false);
      try {
        const { data } = await api.get("/products", { params: { q: code, limit: 1 } });
        if (data && data.length) { addProduct(data[0]); toast.success("Scan ✔", { description: data[0].name }); }
        else toast.error("This barcode isn't linked to any product", { description: code });
      } catch { toast.error("Product search fail"); }
    }} />}
    <AnimatePresence>
      <motion.div key="quick-bill-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:px-4 no-print"
        style={{ background: "rgba(3,3,5,0.7)", backdropFilter: "blur(10px)" }}
        onClick={onClose}
      >
        <motion.div
          data-testid="quick-bill-modal"
          initial={{ opacity: 0, scale: 0.96, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 24 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="glass-strong w-full max-w-3xl rounded-t-3xl sm:rounded-3xl relative overflow-hidden max-h-[96vh] sm:max-h-[92vh] flex flex-col"
        >
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(0,240,255,0.15)" }} />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(112,0,255,0.18)" }} />

          {/* Header */}
          <div className="px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between border-b border-white/5 relative">
            <div>
              <p className="mono text-[10px] uppercase tracking-[0.3em] text-white/45">Quick entry · Non-GST</p>
              <h2 className="display text-2xl mt-1">{created ? `Bill ${created.invoice_number}` : "New cash memo"}</h2>
              {!created && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="mono text-[10px] uppercase tracking-[0.2em] text-white/45">Date</span>
                  <input type="date" max={new Date().toISOString().slice(0, 10)} value={billDate}
                    onChange={(e) => setBillDate(e.target.value)}
                    className="glass rounded-lg px-2.5 py-1 mono text-[12px] bg-transparent outline-none focus:border-cyan-400/40" />
                  {billDate !== new Date().toISOString().slice(0, 10) && (
                    <button onClick={() => setBillDate(new Date().toISOString().slice(0, 10))}
                      className="mono text-[10px] text-cyan-300 hover:underline">aaj</button>
                  )}
                </div>
              )}
            </div>
            <button data-testid="quick-bill-close" onClick={onClose} className="w-9 h-9 rounded-full glass flex items-center justify-center hover:border-white/25">
              <X size={14} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-5 sm:py-6 relative">
            {!created ? (
              <>
                {/* Customer */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Customer</label>
                    <div className="mt-2">
                      <SuggestInput
                        value={customer.name}
                        onChange={(v) => setCustomer({ ...customer, name: v })}
                        onPick={(c) => setCustomer({ ...customer, name: c.name, mobile: c.mobile || customer.mobile })}
                        source="customers"
                        placeholder="Walk-in Customer"
                        className="input w-full"
                        heading="Purane customer"
                        inputProps={{ "data-testid": "quick-bill-customer-name" }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Mobile (optional)</label>
                    <input
                      data-testid="quick-bill-customer-mobile"
                      value={customer.mobile}
                      onChange={(e) => setCustomer({ ...customer, mobile: e.target.value })}
                      placeholder="+91"
                      className="mt-2 input mono"
                    />
                    {custSuggest && (
                      <button type="button" onClick={applySuggest} className="mt-2 w-full text-left glass rounded-lg px-3 py-2 text-[12px] flex items-center justify-between hover:border-cyan-400/40">
                        <span>Previous: <b>{custSuggest.name}</b>{Number(custSuggest.outstanding) > 0 && <span className="text-rose-300 mono text-[11px]"> · Due {fmtINR(custSuggest.outstanding)}</span>}</span>
                        <span className="mono text-[10px] text-cyan-300">USE</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Bill date + offline scan */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Billing date (you can change it)</label>
                    <input
                      data-testid="quick-bill-date"
                      type="date"
                      max={new Date().toISOString().slice(0, 10)}
                      value={billDate}
                      onChange={(e) => setBillDate(e.target.value)}
                      className="mt-2 input mono"
                    />
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Offline copy scan (attach proof)</label>
                    <input ref={scanInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onScanPick} />
                    {!scan ? (
                      <button
                        data-testid="quick-bill-scan"
                        onClick={() => scanInputRef.current?.click()}
                        disabled={uploadingScan}
                        className="mt-2 w-full glass rounded-xl px-4 py-3 text-[13px] flex items-center justify-center gap-2 hover:border-cyan-400/40 disabled:opacity-60"
                      >
                        <FileText size={14} className="text-cyan-300" /> {uploadingScan ? "Uploading…" : "Attach a scan / photo"}
                      </button>
                    ) : (
                      <div className="mt-2 glass rounded-xl p-2 flex items-center gap-3">
                        <a href={scan.url} target="_blank" rel="noreferrer"><img src={scan.url} alt="scan" className="w-12 h-12 rounded-lg object-cover" /></a>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] truncate">{scan.name}</div>
                          {extracting ? <div className="mono text-[10px] text-cyan-300 uppercase tracking-[0.16em] animate-pulse">AI is reading the bill…</div> : <div className="mono text-[10px] text-emerald-300 uppercase tracking-[0.16em]">Attached · verify and save</div>}
                        </div>
                        <button onClick={() => setScan(null)} className="w-8 h-8 rounded-lg hover:bg-rose-500/10 text-white/60 hover:text-rose-300 flex items-center justify-center"><X size={13} /></button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Product search */}
                <div className="relative mt-5">
                  <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Add items</label>
                  <div className="mt-2 relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      ref={searchRef}
                      data-testid="quick-bill-product-search"
                      autoFocus
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      onFocus={() => setShowDrop(true)}
                      onBlur={() => setTimeout(() => setShowDrop(false), 200)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (e.shiftKey || results.length === 0) addManual(); else addProduct(results[0]); } }}
                      placeholder="Type product name / SKU… (Enter to add as-is)"
                      className="w-full pl-9 pr-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder-white/30 outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 transition"
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="mono text-[10px] text-white/40">Enter = first result · Shift+Enter = exactly as typed</span>
                    <button
                      type="button"
                      data-testid="quick-bill-manual-add"
                      onClick={() => { if (!q.trim()) { toast.error("First type the item name"); searchRef.current?.focus(); return; } addManual(); setTimeout(() => searchRef.current?.focus(), 0); }}
                      className="glass rounded-lg px-3 py-1.5 text-[11.5px] hover:border-cyan-400/40"
                    >+ Manual item add</button>
                    <button type="button" onClick={() => setCamOpen(true)} className="glass rounded-lg px-3 py-1.5 text-[11.5px] hover:border-emerald-400/40">📷 Scan</button>
                    <button type="button" onClick={() => setShowSqft((v) => !v)} className="glass rounded-lg px-3 py-1.5 text-[11.5px] hover:border-purple-400/40">📐 sqft</button>
                  </div>
                  {showSqft && (
                    <div className="mt-2 glass rounded-xl p-3 flex flex-wrap items-end gap-2">
                      <div className="flex-1 min-w-[120px]"><label className="mono text-[9.5px] uppercase text-white/45">Item</label><input value={sq.name} onChange={(e) => setSq({ ...sq, name: e.target.value })} className="mt-1 input" placeholder="Curtain / Flooring" /></div>
                      <div className="w-20"><label className="mono text-[9.5px] uppercase text-white/45">L (ft)</label><input type="number" value={sq.l} onChange={(e) => setSq({ ...sq, l: e.target.value })} className="mt-1 input mono" /></div>
                      <div className="w-20"><label className="mono text-[9.5px] uppercase text-white/45">W (ft)</label><input type="number" value={sq.b} onChange={(e) => setSq({ ...sq, b: e.target.value })} className="mt-1 input mono" /></div>
                      <div className="w-24"><label className="mono text-[9.5px] uppercase text-white/45">₹/sqft</label><input type="number" value={sq.rate} onChange={(e) => setSq({ ...sq, rate: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") addSqftItem(); }} className="mt-1 input mono" /></div>
                      <button onClick={addSqftItem} className="pill-neon rounded-lg px-3 py-2 text-[12px]">Add {sq.l && sq.b ? `${(Number(sq.l) * Number(sq.b)).toFixed(1)} sqft` : ""}</button>
                    </div>
                  )}
                  {showDrop && results.length > 0 && (
                    <div onMouseDown={(e) => e.preventDefault()} className="absolute left-0 right-0 top-full mt-1 glass-strong rounded-xl p-2 z-30 max-h-64 overflow-auto">
                      {results.map((p) => (
                        <button
                          key={p.id}
                          data-testid={`quick-bill-product-${p.id}`}
                          onClick={() => { addProduct(p); setTimeout(() => searchRef.current?.focus(), 0); }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/[0.05] flex items-center gap-3"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] truncate">{p.name}</div>
                            <div className="mono text-[10.5px] text-white/45 uppercase tracking-[0.14em]">{p.sku} · stock {fmtNumber(p.stock)} {p.unit}</div>
                          </div>
                          <div className="mono text-[13px] text-cyan-300">{fmtINR(rateFor(p))}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Items */}
                {items.length === 0 ? (
                  <div className="mt-8 border border-dashed border-white/10 rounded-2xl p-10 text-center">
                    <Receipt size={26} className="mx-auto text-white/30" />
                    <p className="mt-3 text-white/60 text-[13px]">No items yet. Search & add above.</p>
                  </div>
                ) : (
                  <div className="mt-5 overflow-x-auto">
                    <table className="min-w-full text-[13px]">
                      <thead>
                        <tr className="mono text-[10px] uppercase tracking-[0.22em] text-white/40 border-b border-white/[0.06]">
                          <th className="text-left py-2 pr-3">Item</th>
                          <th className="text-right py-2 px-2">Qty</th>
                          <th className="text-right py-2 px-2">Rate</th>
                          <th className="text-right py-2 px-2">Total</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it, idx) => {
                          const line = it.qty * it.rate;
                          return (
                            <tr key={idx} className="border-b border-white/[0.04]">
                              <td className="py-2.5 pr-3">
                                <input
                                  value={it.name}
                                  onChange={(e) => updateItem(idx, { name: e.target.value })}
                                  className="input-inline w-full"
                                />
                                {it.sku && <div className="mono text-[10px] text-white/45 mt-0.5">{it.sku}</div>}
                                {lastRates[it.product_id] && (
                                  <button
                                    type="button"
                                    onClick={() => updateItem(idx, { rate: lastRates[it.product_id].rate })}
                                    title="Is rate ko laga do"
                                    className="mono text-[10px] text-amber-300/90 mt-0.5 hover:text-amber-200"
                                  >↩ pichli baar ₹{lastRates[it.product_id].rate} ({lastRates[it.product_id].date.slice(8)}/{lastRates[it.product_id].date.slice(5,7)})</button>
                                )}
                              </td>
                              <td className="text-right px-2">
                                <input
                                  data-testid={`quick-bill-qty-${idx}`}
                                  type="number" min="0" step="0.01"
                                  value={it.qty}
                                  onChange={(e) => updateItem(idx, { qty: Number(e.target.value) })}
                                  className="input-inline w-20 text-right mono"
                                />
                              </td>
                              <td className="text-right px-2">
                                <input
                                  data-testid={`quick-bill-rate-${idx}`}
                                  type="number" min="0" step="0.01"
                                  value={it.rate}
                                  onChange={(e) => updateItem(idx, { rate: Number(e.target.value) })}
                                  className="input-inline w-24 text-right mono"
                                />
                              </td>
                              <td className="text-right px-2 mono">{fmtINR(line)}</td>
                              <td className="pl-2">
                                <button data-testid={`quick-bill-remove-${idx}`} onClick={() => removeItem(idx)} className="w-8 h-8 rounded-lg hover:bg-rose-500/10 hover:text-rose-300 text-white/50 flex items-center justify-center">
                                  <Trash2 size={13} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Payment + total */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                  {items.filter((i) => i.product_id !== "discount-line").length > 0 && (
                  <div className="mt-4 flex items-center justify-end gap-2">
                    <span className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Discount ₹</span>
                    <input type="number" value={discountVal} onChange={(e) => applyDiscount(e.target.value)} placeholder="0" className="input mono w-28 text-right" style={{ width: "7rem" }} />
                  </div>
                )}

                  <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Payment mode</label>
                    <div className="mt-3 grid grid-cols-4 gap-2">
                      {PAYMENT_MODES.map((m) => {
                        const active = paymentMode === m.id;
                        const Ic = m.icon;
                        return (
                          <button
                            key={m.id}
                            data-testid={`quick-bill-mode-${m.id}`}
                            onClick={() => setPaymentMode(m.id)}
                            className={`glass rounded-xl p-2.5 flex flex-col items-start gap-1.5 transition-all text-[11.5px] ${active ? "border-cyan-400/60 shadow-[0_0_22px_rgba(0,240,255,0.2)]" : "hover:border-white/25"}`}
                          >
                            <Ic size={13} className={active ? "text-cyan-300" : "text-white/60"} />
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                    {(((paymentMode === "online" || paymentMode === "bank")) || (splitOn && (Number(splitOnl) || 0) > 0)) && qrAccounts.length > 0 && (
                      <div className="mt-4">
                        <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Receive in — which account will the money go to?</label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {qrAccounts.map((a) => (
                            <button
                              key={a.id}
                              data-testid={`quick-bill-receive-${a.id}`}
                              onClick={() => setReceiveId(a.id)}
                              className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${receiveId === a.id ? "bg-white text-black border-white" : "glass text-white/70 hover:border-white/30"}`}
                            >{a.label}</button>
                          ))}
                        </div>
                        {qrImg && (
                          <div className="mt-3 glass rounded-2xl p-3 flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                            <img src={qrImg} alt="Payment QR" className="w-44 h-44 sm:w-36 sm:h-36 rounded-xl bg-white object-contain p-1.5" />
                            <div className="mono text-[11px] text-white/60 leading-relaxed text-center sm:text-left">
                              Customer se yeh QR scan karwao —<br />
                              payment <span className="text-cyan-300">{qrAccounts.find((a) => a.id === receiveId)?.label}</span> will be credited to the account
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mt-4">
                      <div className="flex items-center justify-between gap-2">
                        <label className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Amount received</label>
                        <button
                          type="button"
                          data-testid="quick-bill-split-toggle"
                          onClick={() => { const v = !splitOn; setSplitOn(v); if (v) { setSplitCash(""); setSplitOnl(String(Math.round(total))); } }}
                          className={`mono text-[10px] px-2.5 py-1 rounded-full border transition-colors ${splitOn ? "bg-white text-black border-white" : "text-cyan-300 border-cyan-400/30 hover:border-cyan-400/60"}`}
                        >{splitOn ? "✓ SPLIT ON" : "Cash + Online?"}</button>
                      </div>
                      {splitOn && (
                        <div className="mt-2 glass rounded-xl p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="mono text-[11px] text-white/60 w-16 shrink-0">💵 Cash</span>
                            <input type="number" value={splitCash} onChange={(e) => setSplitCash(e.target.value)} placeholder="0" className="input mono flex-1 text-right" />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="mono text-[11px] text-white/60 w-16 shrink-0">📱 Online</span>
                            <input type="number" value={splitOnl} onChange={(e) => setSplitOnl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} placeholder="0" className="input mono flex-1 text-right" />
                          </div>
                          <div className="flex flex-wrap gap-1 justify-end">
                            {["upi", "gpay", "phonepe", "bank"].map((m2) => (
                              <button key={m2} type="button" onClick={() => setSplitOnlMode(m2)} className={`px-2 py-1 rounded-md text-[10px] capitalize border ${splitOnlMode === m2 ? "bg-white text-black border-white" : "bg-white/[0.03] text-white/60 border-white/10"}`}>{m2}</button>
                            ))}
                          </div>
                          <div className="mono text-[10.5px] text-white/55 text-right">
                            Kul received: <span className="text-emerald-300">{fmtINR(splitTotal)}</span>
                            {splitTotal < total && <span className="text-rose-300"> · outstanding {fmtINR(total - splitTotal)} due</span>}
                          </div>
                        </div>
                      )}
                      <div className="mt-2 relative">
                        <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                          data-testid="quick-bill-paid"
                          disabled={splitOn}
                          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                          type="number" min="0"
                          value={paid}
                          onChange={(e) => setPaid(Number(e.target.value))}
                          className="input pl-9 mono"
                        />
                      </div>
                      <div className="mt-1 mono text-[10.5px] text-white/50">
                        Balance: <span className={total - paid > 0 ? "text-amber-300" : "text-emerald-300"}>{fmtINR(Math.max(0, total - paid))}</span>
                        {" · "}Change: <span className="text-cyan-300">{fmtINR(Math.max(0, paid - total))}</span>
                      </div>
                    </div>
                  </div>

                  <div className="glass rounded-2xl p-5 flex flex-col justify-center">
                    <p className="mono text-[10px] uppercase tracking-[0.24em] text-white/50">Grand total</p>
                    <div className="display text-4xl mt-2 text-cyan-300">{fmtINR(total)}</div>
                    <div className="mono text-[11px] text-white/45 mt-2">
                      {items.length} item{items.length === 1 ? "" : "s"} · {customer.name}
                    </div>
                    <div className="mono text-[10px] text-white/40 mt-3 uppercase tracking-[0.18em]">Non-GST · Cash memo</div>
                  </div>
                </div>
              </>
            ) : (
              <div>
                <div className="flex items-center justify-center flex-col text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400/30 to-cyan-400/30 flex items-center justify-center">
                    <PartyPopper size={26} className="text-emerald-300" />
                  </div>
                  <h3 className="display text-2xl mt-4">Cash memo {created.invoice_number}</h3>
                  <p className="mono text-[11px] uppercase tracking-[0.24em] text-white/50 mt-1">{fmtDateTime(created.date)}</p>
                </div>
                <div className="cash-memo overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0"><CashMemoSheet ref={printRef} invoice={created} company={company} /></div><ThermalMemo invoice={created} company={company} />
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  <button data-testid="quick-bill-print" onClick={doPrint} className="pill-neon rounded-full px-5 py-2.5 text-[13px] font-medium flex items-center gap-2 hover:scale-[1.02] transition-transform">
                    <Printer size={14} /> Print
                  </button>
                  <button data-testid="quick-bill-thermal" onClick={doThermal} className="glass rounded-full px-5 py-2.5 text-[13px] font-medium flex items-center gap-2 hover:border-white/25">
                    <Printer size={14} /> Thermal
                  </button>
                  <button data-testid="quick-bill-pdf" onClick={downloadPdf} className="glass rounded-full px-5 py-2.5 text-[13px] font-medium flex items-center gap-2 hover:border-white/25">
                    <Download size={14} /> PDF
                  </button>
                  <button data-testid="quick-bill-whatsapp" onClick={sendWhatsApp} className="rounded-full px-5 py-2.5 text-[13px] font-semibold flex items-center gap-2 text-black hover:opacity-90" style={{ background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)" }}>
                    <MessageCircle size={14} /> WhatsApp
                  </button>
                  <button data-testid="quick-bill-new" onClick={newBill} className="rounded-full px-5 py-2.5 text-[13px] font-medium flex items-center gap-2 bg-white text-black hover:bg-cyan-200">
                    <Plus size={14} /> New bill
                  </button>
                  <button onClick={onClose} className="rounded-full px-5 py-2.5 text-[13px] font-medium flex items-center gap-2 text-white/70 hover:text-white">
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {!created && (
            <div className="px-4 sm:px-8 py-3.5 sm:py-4 border-t border-white/5 flex flex-wrap items-center justify-between gap-3 relative">
              <div className="mono text-[11px] text-white/50">
                {items.length === 0 ? "Add items to enable save" : `Total ${fmtINR(total)}`}
              </div>
              <button
                data-testid="quick-bill-save"
                disabled={saving || items.length === 0}
                onClick={submit}
                className="rounded-full px-6 py-2.5 text-[13px] font-semibold flex items-center gap-2 bg-gradient-to-r from-cyan-400 to-purple-500 text-black hover:opacity-90 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save & Print"} <Check size={14} />
              </button>
            </div>
          )}

          <style>{`
            .input { width: 100%; padding: 0.75rem 0.9rem; border-radius: 0.75rem; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); color: #fff; outline: none; font-size: 13px; }
            .input:focus { border-color: rgba(0,240,255,0.55); box-shadow: 0 0 0 3px rgba(0,240,255,0.16); }
            .input-inline { padding: 0.4rem 0.55rem; border-radius: 0.5rem; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); color: #fff; outline: none; font-size: 12.5px; }
          `}</style>
        </motion.div>
      </motion.div>
    </AnimatePresence>

    {created && (
      <div id="print-invoice" className="hidden print:block">
        <CashMemoSheet invoice={created} company={company} plain />
      </div>
    )}
    </>
  );
}

// eslint-disable-next-line react/display-name
const NUM_ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const NUM_TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function twoDigitWords(n) {
  if (n < 20) return NUM_ONES[n];
  return NUM_TENS[Math.floor(n / 10)] + (n % 10 ? " " + NUM_ONES[n % 10] : "");
}

function amountWords(amt) {
  const v = Math.abs(Number(amt) || 0);
  const n = Math.floor(v);
  const paise = Math.round((v - n) * 100);
  if (!n && !paise) return "Zero";
  const parts = [];
  const cr = Math.floor(n / 10000000);
  const lk = Math.floor((n % 10000000) / 100000);
  const th = Math.floor((n % 100000) / 1000);
  const hd = Math.floor((n % 1000) / 100);
  const rest = n % 100;
  if (cr) parts.push(twoDigitWords(cr) + " Crore");
  if (lk) parts.push(twoDigitWords(lk) + " Lakh");
  if (th) parts.push(twoDigitWords(th) + " Thousand");
  if (hd) parts.push(NUM_ONES[hd] + " Hundred");
  if (rest) parts.push((parts.length ? "and " : "") + twoDigitWords(rest));
  let out = parts.join(" ");
  if (paise) out += (out ? " and " : "") + twoDigitWords(paise) + " Paise";
  return out.trim();
}

const rupPart = (v) => Math.floor(Math.abs(Number(v) || 0));
const paisePart = (v) => String(Math.round((Math.abs(Number(v) || 0) - rupPart(v)) * 100)).padStart(2, "0");
const ddmmyy = (d) => {
  const s = String(d || "");
  return s.length >= 10 ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(2, 4)}` : "";
};

const CashMemoSheet = forwardRef(function CashMemoSheet({ invoice, company, plain }, ref) {
  if (!invoice || !company) return null;
  const items = invoice.items || [];
  const blanks = Math.max(0, 14 - items.length);
  const due = Number(invoice.due) || 0;
  const prev = Number(invoice.previous_due) || 0;
  const addr = [company.address, company.city,
    [company.state, company.pincode].filter(Boolean).join("- ")].filter(Boolean).join(" :: ");

  const B = "1px solid #000";
  const cell = { border: B, padding: "2px 5px", fontSize: 12.5, lineHeight: 1.2, height: 21, verticalAlign: "middle" };
  const head = { ...cell, fontWeight: 700, textAlign: "center", fontSize: 11, height: 19 };

  return (
    <div ref={ref} className="memo-pad mx-auto"
      style={{
        width: "100%", maxWidth: 600, background: "#fff", color: "#000",
        fontFamily: "Arial, Helvetica, sans-serif", padding: "12px 15px 8px",
        borderRadius: plain ? 0 : 10, border: plain ? "none" : "1px solid #ddd",
      }}
    >
      <div style={{ textAlign: "center", fontSize: 10.5, letterSpacing: 1.2, fontWeight: 700, textDecoration: "underline" }}>
        CASH MEMO
      </div>

      {company.logo && (
        <div style={{ textAlign: "center", marginTop: 1 }}>
          <img src={company.logo} alt="" style={{ maxHeight: 28 }} />
        </div>
      )}
      <div style={{ textAlign: "center", fontWeight: 800, fontSize: 25, letterSpacing: 0.5, lineHeight: 1.05 }}>
        {company.name || "INTERIOR TRADERS"}
      </div>
      {addr && <div style={{ textAlign: "center", fontSize: 11 }}>{addr}</div>}
      {company.gstin && <div style={{ textAlign: "center", fontSize: 11 }}>GSTIN : {company.gstin}</div>}

      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 2 }}>
        <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: 1, whiteSpace: "nowrap" }}>
          {String(invoice.invoice_number || "").split("/").pop()}
        </span>
        <span style={{ fontSize: 11, flex: 1 }}>{company.phone ? `Mob. No. : ${company.phone}` : ""}</span>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginTop: 1 }}>
        <span>Sl. No. <b>{invoice.invoice_number}</b></span>
        <span>Date&nbsp;<b style={{ borderBottom: "1px solid #000", padding: "0 12px" }}>{ddmmyy(invoice.date)}</b></span>
      </div>

      <div style={{ fontSize: 12, marginTop: 3 }}>
        Name :&nbsp;
        <span style={{ borderBottom: "1px dotted #000", display: "inline-block", minWidth: "80%", fontWeight: 600 }}>
          &nbsp;{invoice.customer_name || ""}{invoice.customer_mobile ? `   ${invoice.customer_mobile}` : ""}
        </span>
      </div>
      <div style={{ fontSize: 12, marginTop: 2, marginBottom: 4 }}>
        Address :&nbsp;
        <span style={{ borderBottom: "1px dotted #000", display: "inline-block", minWidth: "76%" }}>
          &nbsp;{invoice.customer_address || ""}
        </span>
      </div>

      <div className="overflow-x-auto"><table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <thead>
          <tr>
            <th style={{ ...head, width: "13%" }}>Qnty.</th>
            <th style={{ ...head, width: "44%" }}>PARTICULARS</th>
            <th style={{ ...head, width: "16%" }}>Rate</th>
            <th style={{ ...head, width: "20%", padding: 0 }}>
              <div style={{ borderBottom: B, fontSize: 10 }}>Amount</div>
              <div style={{ fontSize: 10.5 }}>Rs.</div>
            </th>
            <th style={{ ...head, width: "7%" }}>P.</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const amt = Number(it.total ?? it.line_total ?? (Number(it.qty) || 0) * (Number(it.rate) || 0)) || 0;
            return (
              <tr key={i}>
                <td style={{ ...cell, textAlign: "center" }}>
                  {it.qty}{it.unit && it.unit !== "piece" ? ` ${it.unit}` : ""}
                </td>
                <td style={{ ...cell, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{it.name}</td>
                <td style={{ ...cell, textAlign: "right" }}>{Number(it.rate) ? rupPart(it.rate) + "/-" : ""}</td>
                <td style={{ ...cell, textAlign: "right" }}>{rupPart(amt)}</td>
                <td style={{ ...cell, textAlign: "right" }}>{paisePart(amt) !== "00" ? paisePart(amt) : ""}</td>
              </tr>
            );
          })}
          {Array.from({ length: blanks }).map((_, i) => (
            <tr key={`blank-${i}`}>
              <td style={cell}>&nbsp;</td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
            </tr>
          ))}
          {prev > 0 && (
            <tr>
              <td style={cell}></td>
              <td style={{ ...cell, textAlign: "right", fontSize: 11.5 }}>If balance remains</td>
              <td style={cell}></td>
              <td style={{ ...cell, textAlign: "right" }}>{rupPart(prev)}</td>
              <td style={{ ...cell, textAlign: "right" }}>{paisePart(prev) !== "00" ? paisePart(prev) : ""}</td>
            </tr>
          )}
          <tr>
            <td style={cell}></td>
            <td style={cell}></td>
            <td style={{ ...cell, textAlign: "center", fontWeight: 800, background: "#000", color: "#fff" }}>TOTAL</td>
            <td style={{ ...cell, textAlign: "right", fontWeight: 800 }}>{rupPart(invoice.total)}</td>
            <td style={{ ...cell, textAlign: "right", fontWeight: 800 }}>
              {paisePart(invoice.total) !== "00" ? paisePart(invoice.total) : ""}
            </td>
          </tr>
          {due > 0 && (
            <>
              <tr>
                <td style={cell}></td>
                <td style={cell}></td>
                <td style={{ ...cell, textAlign: "center", fontSize: 11.5 }}>Paid</td>
                <td style={{ ...cell, textAlign: "right" }}>{rupPart(invoice.paid)}</td>
                <td style={{ ...cell, textAlign: "right" }}>{paisePart(invoice.paid) !== "00" ? paisePart(invoice.paid) : ""}</td>
              </tr>
              <tr>
                <td style={cell}></td>
                <td style={cell}></td>
                <td style={{ ...cell, textAlign: "center", fontWeight: 700, fontSize: 11.5 }}>Pending</td>
                <td style={{ ...cell, textAlign: "right", fontWeight: 700 }}>{rupPart(due)}</td>
                <td style={{ ...cell, textAlign: "right", fontWeight: 700 }}>{paisePart(due) !== "00" ? paisePart(due) : ""}</td>
              </tr>
            </>
          )}
        </tbody>
      </table></div>

      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, marginTop: 2 }}>
        <i style={{ fontWeight: 700 }}>{company.invoice_footer || "Thank you for visit again"}</i>
        <span style={{ fontSize: 9.5 }}>E. &amp; O. E</span>
      </div>
      <div style={{ fontSize: 11, marginTop: 2, borderBottom: "1px dotted #000", paddingBottom: 1 }}>
        <b>Rupees in words :</b> {amountWords(invoice.total)} only.
      </div>
      {company.show_bank_on_bill && (company.account_number || company.upi_id) && (
        <div style={{ fontSize: 9.5, marginTop: 2, lineHeight: 1.3 }}>
          {[company.bank_name, company.account_number ? `A/c: ${company.account_number}` : "",
            company.ifsc ? `IFSC: ${company.ifsc}` : "", company.upi_id ? `UPI: ${company.upi_id}` : ""]
            .filter(Boolean).join(" · ")}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 22, fontSize: 10, fontWeight: 700 }}>
        <span>CUSTOMER SIGNATURE</span>
        <span>For, {(company.name || "INTERIOR TRADERS").toUpperCase()}</span>
      </div>
    </div>
  );
});
function ThermalMemo({ invoice, company }) {
  if (!invoice) return null;
  const line = "-".repeat(32);
  return (
    <div className="thermal-memo" style={{ width: "80mm", maxWidth: "80mm", background: "#fff", color: "#000", fontFamily: "monospace", fontSize: "11px", lineHeight: 1.35, padding: "4mm" }}>
      {company?.logo && <div style={{ textAlign: "center" }}><img src={company.logo} alt="" style={{ maxHeight: 34, margin: "0 auto 2px" }} /></div>}
      <div style={{ textAlign: "center", fontWeight: 700, fontSize: "13px" }}>{company?.name || "Interior Traders"}</div>
      <div style={{ textAlign: "center" }}>{company?.phone || ""}</div>
      <div>{line}</div>
      <div>Bill: {invoice.invoice_number}</div>
      <div>Date: {new Date(invoice.date).toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
      <div>Cust: {invoice.customer_name}</div>
      <div>{line}</div>
      {(invoice.items || []).map((it, i) => (
        <div key={i}>
          <div>{it.name.slice(0, 30)}</div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{it.qty} x {Number(it.rate).toFixed(0)}</span>
            <span>{(Number(it.qty) * Number(it.rate)).toFixed(0)}</span>
          </div>
        </div>
      ))}
      <div>{line}</div>
      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "12.5px" }}><span>TOTAL</span><span>Rs.{Number(invoice.total).toFixed(0)}</span></div>
      <div style={{ display: "flex", justifyContent: "space-between" }}><span>PAID ({(invoice.payment_mode || "").toUpperCase()})</span><span>{Number(invoice.paid).toFixed(0)}</span></div>
      {Number(invoice.due) > 0 && <div style={{ display: "flex", justifyContent: "space-between" }}><span>DUE</span><span>{Number(invoice.due).toFixed(0)}</span></div>}
      {Number(invoice.previous_due) > 0 && <div style={{ display: "flex", justifyContent: "space-between" }}><span>PREVIOUS BALANCE</span><span>{Number(invoice.previous_due).toFixed(0)}</span></div>}
      <div>{line}</div>
      <div style={{ textAlign: "center" }}>Thank you! Come again 🙏</div>
    </div>
  );
}


function BarcodeCam({ onClose, onCode }) {
  const videoRef = useRef(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let stream, raf, stopped = false;
    (async () => {
      if (!("BarcodeDetector" in window)) {
        setErr("This browser doesn't support camera scan. It works in Android Chrome — or use a scanner gun.");
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
        // eslint-disable-next-line no-undef
        const det = new BarcodeDetector({ formats: ["code_128", "ean_13", "ean_8", "upc_a", "upc_e", "code_39", "qr_code"] });
        const tick = async () => {
          if (stopped || !videoRef.current) return;
          try {
            const codes = await det.detect(videoRef.current);
            if (codes && codes.length) { onCode(codes[0].rawValue); return; }
          } catch {}
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      } catch (e) {
        setErr("Camera didn't open — grant permission or check HTTPS.");
      }
    })();
    return () => { stopped = true; if (raf) cancelAnimationFrame(raf); if (stream) stream.getTracks().forEach((t) => t.stop()); };
  }, [onCode]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[140] flex items-center justify-center p-4"
      style={{ background: "rgba(3,3,5,0.9)" }} onClick={onClose}>
      <motion.div initial={{ scale: 0.97 }} animate={{ scale: 1 }} onClick={(e) => e.stopPropagation()}
        className="glass-strong rounded-3xl overflow-hidden w-full max-w-md">
        <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
          <span className="mono text-[11px] uppercase tracking-[0.24em] text-white/60">Barcode scan</span>
          <button onClick={onClose} className="w-8 h-8 rounded-full glass flex items-center justify-center"><X size={13} /></button>
        </div>
        <div className="p-4">
          {err ? (
            <p className="mono text-[12px] text-amber-300 leading-relaxed py-6 text-center">{err}</p>
          ) : (
            <>
              <video ref={videoRef} playsInline muted className="w-full rounded-xl bg-black" style={{ maxHeight: "60vh" }} />
              <p className="mono text-[11px] text-white/50 mt-3 text-center">Barcode ko camera ke saamne rakho…</p>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
